/**
 * PhotoReviewQueue — AOS-LID-03: Institution-Managed Student Photo Review
 *
 * Route: /campus/id/photo-review  (staff: registrar, institution_admin, super_admin)
 *
 * Features:
 *  - Status filter tabs (PENDING_REVIEW, APPROVED, REJECTED, ALL)
 *  - Student search (name or student number)
 *  - Paginated table of submissions
 *  - Authenticated photo preview (proxied, no raw storage URL)
 *  - Approve / Reject (with required reason) actions
 *  - Real-time feedback toasts
 *  - Mobile-responsive layout
 *
 * Security:
 *  - campus.id.photo.review permission required (enforced backend)
 *  - Photo preview fetched via authenticated backend proxy
 *  - Raw storage_path never sent to browser
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  ShieldAlert,
  Sliders,
  User,
  XCircle,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// ─── Status definitions ───────────────────────────────────────────────────────
const STATUS_CONFIG = {
  PENDING_REVIEW: {
    label: "Pending Review",
    badge: "bg-amber-500/20 text-amber-300 border border-amber-500/30",
    icon: Clock,
  },
  APPROVED: {
    label: "Approved",
    badge: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
    icon: CheckCircle2,
  },
  REJECTED: {
    label: "Rejected",
    badge: "bg-red-500/20 text-red-300 border border-red-500/30",
    icon: XCircle,
  },
  SUPERSEDED: {
    label: "Superseded",
    badge: "bg-slate-500/20 text-slate-400 border border-slate-600/30",
    icon: ShieldAlert,
  },
  WITHDRAWN: {
    label: "Withdrawn",
    badge: "bg-slate-600/20 text-slate-500 border border-slate-700/30",
    icon: XCircle,
  },
};

const TAB_FILTERS = [
  { key: "PENDING_REVIEW", label: "Pending", count: null },
  { key: "APPROVED",       label: "Approved", count: null },
  { key: "REJECTED",       label: "Rejected", count: null },
  { key: "ALL",            label: "All",      count: null },
];

const PAGE_SIZE = 20;

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || {
    label: status,
    badge: "bg-slate-700/30 text-slate-400",
    icon: Sliders,
  };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${cfg.badge}`}>
      <Icon className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
}

// ─── Photo Preview Modal ──────────────────────────────────────────────────────
function PhotoPreviewModal({ reviewId, studentName, onClose, token }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reviewId || !token) return;
    setLoading(true);
    setError(null);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/campus/id/photo/review/${reviewId}/preview`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        setSrc(URL.createObjectURL(blob));
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [reviewId, token]);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="photo-preview-modal"
    >
      <div
        className="bg-slate-900 border border-white/10 rounded-2xl p-4 max-w-xs w-full flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-white font-semibold text-sm truncate w-full text-center">
          {studentName}
        </p>
        {loading ? (
          <div className="w-48 h-48 bg-white/5 rounded-xl flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="w-48 h-48 bg-red-950/30 rounded-xl flex flex-col items-center justify-center gap-2">
            <AlertCircle className="w-8 h-8 text-red-400" />
            <p className="text-red-400 text-xs text-center">Photo unavailable</p>
          </div>
        ) : (
          <img
            src={src}
            alt={studentName}
            className="w-48 h-48 object-cover rounded-xl ring-2 ring-white/10"
            data-testid="preview-photo-img"
          />
        )}
        <button
          onClick={onClose}
          className="text-xs text-slate-400 hover:text-white transition-colors"
          data-testid="close-preview-btn"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ─── Reject Modal ─────────────────────────────────────────────────────────────
function RejectModal({ reviewId, studentName, onClose, onConfirm, loading }) {
  const [reason, setReason] = useState("");

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="reject-modal"
    >
      <div className="bg-slate-900 border border-white/10 rounded-2xl p-5 max-w-sm w-full flex flex-col gap-4">
        <div>
          <h3 className="text-white font-semibold text-base">Reject Photo</h3>
          <p className="text-slate-400 text-xs mt-1">
            For <span className="text-white font-medium">{studentName}</span>
          </p>
        </div>
        <div>
          <label className="text-slate-300 text-xs font-medium block mb-1">
            Rejection reason <span className="text-red-400">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Photo does not show a clear face, Background is not plain…"
            className="w-full bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-xs resize-none h-24 focus:outline-none focus:ring-1 focus:ring-sky-500 placeholder-slate-600"
            data-testid="rejection-reason-input"
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-1.5 rounded-lg text-xs text-slate-400 hover:text-white transition-colors disabled:opacity-50"
            data-testid="cancel-reject-btn"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reviewId, reason)}
            disabled={!reason.trim() || loading}
            className="px-4 py-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5"
            data-testid="confirm-reject-btn"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, type, onDismiss }) {
  useEffect(() => {
    const id = setTimeout(onDismiss, 4000);
    return () => clearTimeout(id);
  }, [onDismiss]);

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-4 py-2 rounded-xl shadow-xl text-sm font-medium ${
        type === "success"
          ? "bg-emerald-700 text-white"
          : "bg-red-700 text-white"
      }`}
      data-testid="review-toast"
    >
      {type === "success"
        ? <CheckCircle2 className="w-4 h-4" />
        : <AlertCircle className="w-4 h-4" />
      }
      {message}
    </div>
  );
}

// ─── Submission Row ───────────────────────────────────────────────────────────
function SubmissionRow({ item, onPreview, onApprove, onReject, actionLoading }) {
  const isActing = actionLoading === item.review_id;

  return (
    <div
      className="bg-slate-800/40 border border-white/5 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid={`review-row-${item.review_id}`}
    >
      {/* Avatar / Preview button */}
      <button
        onClick={() => onPreview(item)}
        className="w-12 h-12 rounded-xl bg-slate-700/50 border border-white/10 flex items-center justify-center hover:bg-slate-600/50 transition-colors shrink-0 group"
        data-testid={`preview-btn-${item.review_id}`}
        title="Preview photo"
      >
        <User className="w-6 h-6 text-slate-400 group-hover:text-white transition-colors" />
      </button>

      {/* Student info */}
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold text-sm truncate" data-testid={`student-name-${item.review_id}`}>
          {item.student_display_name}
        </p>
        <p className="text-slate-500 text-xs font-mono">{item.student_number}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <StatusBadge status={item.status} />
          <span className="text-slate-600 text-[10px]">
            {new Date(item.submitted_at).toLocaleDateString("en-IN", {
              day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
            })}
          </span>
          {item.file_content_type && (
            <span className="text-slate-700 text-[10px] uppercase">
              {item.file_content_type.split("/")[1]}
            </span>
          )}
          {item.file_size_bytes && (
            <span className="text-slate-700 text-[10px]">
              {(item.file_size_bytes / 1024).toFixed(0)} KB
            </span>
          )}
        </div>
        {item.rejection_reason && (
          <p className="text-red-400/70 text-[10px] mt-1 line-clamp-2">
            Reason: {item.rejection_reason}
          </p>
        )}
        {item.reviewed_by_email && (
          <p className="text-slate-600 text-[10px] mt-0.5">
            Reviewed by: {item.reviewed_by_email}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => onPreview(item)}
          className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white transition-colors"
          title="Preview"
          data-testid={`preview-icon-btn-${item.review_id}`}
        >
          <Eye className="w-4 h-4" />
        </button>

        {item.status === "PENDING_REVIEW" && (
          <>
            <button
              onClick={() => onApprove(item.review_id, item.student_display_name)}
              disabled={isActing}
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600/80 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
              data-testid={`approve-btn-${item.review_id}`}
            >
              {isActing ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3 h-3" />
              )}
              Approve
            </button>
            <button
              onClick={() => onReject(item)}
              disabled={isActing}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-700/60 hover:bg-red-600 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
              data-testid={`reject-btn-${item.review_id}`}
            >
              <XCircle className="w-3 h-3" />
              Reject
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PhotoReviewQueue() {
  const { token } = useAuth();

  const [statusFilter, setStatusFilter] = useState("PENDING_REVIEW");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [previewItem, setPreviewItem] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const [toast, setToast] = useState(null);
  const searchTimer = useRef(null);

  // ── Debounced search ──────────────────────────────────────────────────────
  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setOffset(0);
    }, 400);
  };

  // ── Fetch queue ───────────────────────────────────────────────────────────
  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        limit: PAGE_SIZE,
        offset,
      });
      if (debouncedSearch) params.set("student_search", debouncedSearch);

      const { data } = await api.get(`/campus/id/photo/review-queue?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load review queue.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch, offset]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Reset offset when filter or search changes
  useEffect(() => { setOffset(0); }, [statusFilter, debouncedSearch]);

  // ── Approve ───────────────────────────────────────────────────────────────
  const handleApprove = async (reviewId, studentName) => {
    setActionLoading(reviewId);
    try {
      await api.post(`/campus/id/photo/review/${reviewId}/approve`);
      setToast({ message: `Photo approved for ${studentName}`, type: "success" });
      fetchQueue();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || "Approval failed");
      setToast({ message: msg, type: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  // ── Reject ────────────────────────────────────────────────────────────────
  const handleRejectConfirm = async (reviewId, reason) => {
    setActionLoading(reviewId);
    try {
      await api.post(`/campus/id/photo/review/${reviewId}/reject`, {
        rejection_reason: reason,
      });
      setToast({ message: "Photo submission rejected", type: "success" });
      setRejectItem(null);
      fetchQueue();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || "Rejection failed");
      setToast({ message: msg, type: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4 py-6 sm:px-6"
      data-testid="photo-review-queue-page"
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-white font-bold text-2xl sm:text-3xl">Photo Review Queue</h1>
          <p className="text-slate-500 text-sm mt-1">
            Review student photo submissions for institutional ID cards
          </p>
        </div>

        {/* ── Controls ─────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by name or student number…"
              className="w-full bg-slate-800/60 border border-white/10 rounded-xl pl-9 pr-4 py-2 text-white text-sm placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-500"
              data-testid="search-input"
            />
          </div>
          {/* Refresh */}
          <button
            onClick={() => fetchQueue()}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-800/60 border border-white/10 rounded-xl text-slate-400 hover:text-white transition-colors text-sm"
            data-testid="refresh-queue-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* ── Status Tabs ───────────────────────────────────────────────── */}
        <div className="flex gap-1 mb-5 overflow-x-auto pb-1" data-testid="status-tabs">
          {TAB_FILTERS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                statusFilter === tab.key
                  ? "bg-sky-600 text-white"
                  : "bg-slate-800/60 text-slate-400 hover:text-white border border-white/5"
              }`}
              data-testid={`tab-${tab.key.toLowerCase()}`}
            >
              {tab.label}
              {tab.key === "PENDING_REVIEW" && total > 0 && statusFilter === "PENDING_REVIEW" && (
                <span className="ml-1.5 bg-amber-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                  {total}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Content ───────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
            <p className="text-slate-500 text-sm">Loading submissions…</p>
          </div>
        ) : error ? (
          <div
            className="bg-red-950/30 border border-red-800/40 rounded-xl p-6 flex flex-col items-center gap-2"
            data-testid="queue-error"
          >
            <AlertCircle className="w-8 h-8 text-red-400" />
            <p className="text-red-300 text-sm text-center">{
              typeof error === "object" ? error.message || String(error) : error
            }</p>
            <button
              onClick={() => fetchQueue()}
              className="text-xs text-sky-400 hover:text-sky-300 underline"
            >
              Try again
            </button>
          </div>
        ) : items.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-20 gap-2 text-center"
            data-testid="empty-queue"
          >
            <CheckCircle2 className="w-10 h-10 text-slate-700" />
            <p className="text-slate-500 text-sm font-medium">
              {statusFilter === "PENDING_REVIEW"
                ? "No pending photo submissions"
                : `No ${statusFilter.toLowerCase().replace("_", " ")} submissions`}
            </p>
            {debouncedSearch && (
              <p className="text-slate-600 text-xs">
                No results for &ldquo;{debouncedSearch}&rdquo;
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Results count */}
            <p className="text-slate-600 text-xs mb-3" data-testid="result-count">
              Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total} submission{total !== 1 ? "s" : ""}
            </p>

            {/* List */}
            <div className="flex flex-col gap-3" data-testid="review-list">
              {items.map((item) => (
                <SubmissionRow
                  key={item.review_id}
                  item={item}
                  onPreview={setPreviewItem}
                  onApprove={handleApprove}
                  onReject={setRejectItem}
                  actionLoading={actionLoading}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-6" data-testid="pagination">
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  className="p-2 rounded-lg bg-slate-800/60 border border-white/10 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-slate-400 text-xs">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={currentPage >= totalPages}
                  className="p-2 rounded-lg bg-slate-800/60 border border-white/10 text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Photo Preview Modal ───────────────────────────────────────── */}
      {previewItem && (
        <PhotoPreviewModal
          reviewId={previewItem.review_id}
          studentName={previewItem.student_display_name}
          onClose={() => setPreviewItem(null)}
          token={token}
        />
      )}

      {/* ── Reject Modal ─────────────────────────────────────────────── */}
      {rejectItem && (
        <RejectModal
          reviewId={rejectItem.review_id}
          studentName={rejectItem.student_display_name}
          onClose={() => setRejectItem(null)}
          onConfirm={handleRejectConfirm}
          loading={actionLoading === rejectItem.review_id}
        />
      )}

      {/* ── Toast ────────────────────────────────────────────────────── */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
